'use client';

import { c as _c } from "react/compiler-runtime";
import { type RouterOutputs } from '@shared/all/domains/trpc';
import { ArrowUpRight, ChevronUp, Lock, MessageCircle, ShoppingBag } from 'lucide-react';
import Link from 'next/link';
import { getS3ObjectURL } from '../../utils/s3';
import Avatar from './Avatar';
export type SemanticResult = RouterOutputs['search']['semantic']['results'][number];
const priceLabel = (cents: number, discount: number) => {
  const net = discount > 0 ? cents * (1 - discount / 100) : cents;
  return `$${(net / 100).toFixed(2)}`;
};
const Dot = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="text-textSecondary/40">·</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

// Results arrive flat and render-ready (title/description are already
// plain-text server-side); this only picks the per-type route, avatar
// variant, and metadata line (nodes so posts can show engagement inline).
const describe = (r: SemanticResult): {
  href: string;
  avatar: React.ReactNode;
  primary: string;
  meta: React.ReactNode;
  thumb?: string | null;
} => {
  switch (r.type) {
    case 'post':
      return {
        href: `/post/${r.slug}`,
        thumb: r.imageKey ? getS3ObjectURL(r.imageKey) : null,
        avatar: <Avatar size={48} avatarKey={r.avatarKey} robohash={r.robohash} userId={r.id} isBot={r.tag === 'BOT'} tag={r.tag ?? undefined} />,
        primary: r.title,
        meta: <>
            {/* Only the viewer can see this one — a file they synced from
                their own drive, sitting among otherwise public results. */}
            {r.isPrivate && <>
                <span className="inline-flex items-center gap-1">
                  <Lock className="size-3" strokeWidth={2} />
                  Private
                </span>
                <Dot />
              </>}
            <span className="truncate">@{r.username}</span>
            {r.spaceSlug && <>
                <Dot />
                <span className="truncate">s/{r.spaceSlug}</span>
              </>}
            <Dot />
            <span className="inline-flex items-center gap-0.5 tabular-nums">
              <ChevronUp className="size-3.5" strokeWidth={2.5} />
              {r.voteCount}
            </span>
            {(r.repliesCount ?? 0) > 0 && <span className="inline-flex items-center gap-1 tabular-nums">
                <MessageCircle className="size-3" strokeWidth={2} />
                {r.repliesCount}
              </span>}
          </>
      };
    case 'user':
      return {
        href: `/user/${r.username}`,
        avatar: <Avatar size={48} avatarKey={r.avatarKey} robohash={r.robohash} userId={r.id} isBot={r.tag === 'BOT'} tag={r.tag ?? undefined} />,
        primary: r.title,
        meta: <>
            <span className="truncate">@{r.username}</span>
            {r.description && <>
                <Dot />
                <span className="truncate">{r.description}</span>
              </>}
          </>
      };
    case 'space':
      return {
        href: `/s/${r.slug}`,
        avatar: <Avatar size={48} spaceAvatarKey={r.avatarKey} />,
        primary: r.title,
        meta: <span className="truncate">{r.description ?? `s/${r.slug}`}</span>
      };
    case 'factiii':
      return {
        href: `/fids/${r.id}`,
        avatar: <Avatar size={48} factiiiAvatarKey={r.avatarKey} factiiiId={r.id} />,
        primary: r.title,
        meta: <span className="truncate">{r.description ?? 'Factiii'}</span>
      };
    case 'product':
      return {
        href: `/s/${r.spaceSlug}`,
        avatar: r.imageKey ?
        // eslint-disable-next-line @next/next/no-img-element
        <img src={getS3ObjectURL(r.imageKey)} alt="" className="size-12 rounded-xl object-cover" /> : <div className="flex size-12 items-center justify-center rounded-xl bg-background-light text-textSecondary">
            <ShoppingBag className="size-5" />
          </div>,
        primary: r.title,
        meta: <>
            <span className="font-emphasis text-textPrimary">
              {priceLabel(r.price ?? 0, r.discount ?? 0)}
            </span>
            {r.description && <>
                <Dot />
                <span className="truncate">{r.description}</span>
              </>}
          </>
      };
  }
};
type Props = {
  result: SemanticResult;
  index: number;
  // Scoped results render inside the header dropdown, which has to close when
  // the row navigates away.
  onNavigate?: () => void;
  compact?: boolean;
};

/**
 * One row of the unified search stream. Shared by /search and the header's
 * page-scoped dropdown so a result looks the same wherever it surfaces.
 */
const SearchResultRow = t0 => {
  const $ = _c(24);
  const {
    result,
    index,
    onNavigate,
    compact
  } = t0;
  let t1;
  if ($[0] !== result) {
    t1 = describe(result);
    $[0] = result;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    href,
    avatar,
    primary,
    meta,
    thumb
  } = t1;
  const t2 = `${Math.min(index, 14) * 26}ms`;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      animationDelay: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `group flex animate-search-row items-center gap-4 rounded-2xl outline-none transition-all duration-150 hover:bg-background-light focus-visible:bg-background-light ${compact ? "px-2 py-2" : "px-3 py-3"}`;
  let t5;
  if ($[4] !== avatar) {
    t5 = <div className="shrink-0">{avatar}</div>;
    $[4] = avatar;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== primary) {
    t6 = <div className="truncate text-[15px] font-emphasis leading-snug text-textPrimary">{primary}</div>;
    $[6] = primary;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== meta) {
    t7 = <div className="mt-1 flex items-center gap-2 overflow-hidden text-[13px] leading-none text-textSecondary">{meta}</div>;
    $[8] = meta;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <div className="min-w-0 flex-1">{t6}{t7}</div>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== thumb) {
    t9 = thumb && <img src={thumb} alt="" className="size-14 shrink-0 rounded-lg object-cover" />;
    $[13] = thumb;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ArrowUpRight className="size-5 shrink-0 -translate-x-1.5 text-textSecondary opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100" />;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== href || $[17] !== onNavigate || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t8 || $[22] !== t9) {
    t11 = <Link href={href} onClick={onNavigate} style={t3} className={t4}>{t5}{t8}{t9}{t10}</Link>;
    $[16] = href;
    $[17] = onNavigate;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t8;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
};
export default SearchResultRow;